/**
 * Subscription helpers — giới hạn sản phẩm theo gói chủ nhà.
 */

export type SubscriptionLevel = 'free' | 'basic' | 'standard' | 'premium' | 'theo_yeu_cau';

export type OwnerSubscriptionInfo = {
  subscription_level?: string | null;
  subscription_end_date?: string | null;
  is_subscription_active?: boolean;
};

const LEGACY_SUBSCRIPTION_LEVEL_MAP: Record<string, SubscriptionLevel> = {
  pro: 'basic',
  elite: 'standard',
};

export const PAID_SUBSCRIPTION_TIERS = new Set<string>([
  'basic',
  'standard',
  'premium',
  'theo_yeu_cau',
  'pro',
  'elite',
]);

/** Infinity = không giới hạn số sản phẩm. */
export const SUBSCRIPTION_PROPERTY_LIMITS: Record<string, number> = {
  free: 0,
  basic: 15,
  standard: 30,
  premium: 50,
  theo_yeu_cau: Number.POSITIVE_INFINITY,
  // legacy
  pro: 15,
  elite: 30,
};

/** Gói theo yêu cầu: 10.000đ/phòng, chọn 50–1.000 phòng (bước 10). 1 năm −15%, 2 năm −20%. */
export const ON_REQUEST_PLAN_PRICING = {
  roomPriceVnd: 10_000,
  minRooms: 50,
  maxRooms: 1000,
  stepRooms: 10,
  discountByMonths: { 12: 15, 24: 20 } as const,
} as const;

export function onRequestDiscountPercent(months: number): number {
  const rates = ON_REQUEST_PLAN_PRICING.discountByMonths;
  return rates[months as keyof typeof rates] ?? 0;
}

export function calculateOnRequestPlanPrice(roomCount: number, months = 1): {
  roomCount: number;
  months: number;
  unitPriceVnd: number;
  discountPercent: number;
  originalTotalVnd: number;
  totalVnd: number;
} {
  const step = ON_REQUEST_PLAN_PRICING.stepRooms;
  const min = ON_REQUEST_PLAN_PRICING.minRooms;
  const max = ON_REQUEST_PLAN_PRICING.maxRooms;
  const raw = Number.isFinite(roomCount) ? Math.floor(roomCount) : min;
  const snapped = Math.round(raw / step) * step;
  const rooms = Math.min(max, Math.max(min, snapped));
  const durationMonths = Number.isFinite(months) ? Math.max(1, Math.floor(months)) : 1;
  const discountPercent = onRequestDiscountPercent(durationMonths);
  const originalTotalVnd = rooms * ON_REQUEST_PLAN_PRICING.roomPriceVnd * durationMonths;
  return {
    roomCount: rooms,
    months: durationMonths,
    unitPriceVnd: ON_REQUEST_PLAN_PRICING.roomPriceVnd,
    discountPercent,
    originalTotalVnd,
    totalVnd: Math.round(originalTotalVnd * (1 - discountPercent / 100)),
  };
}

export function formatVnd(amount: number): string {
  return `${amount.toLocaleString('vi-VN')}đ`;
}

/** Thời hạn chủ nhà có thể chọn khi nâng cấp/gia hạn gói trả phí. */
export const SUBSCRIPTION_PURCHASE_DURATIONS = [
  { months: 1, days: 30, label: '1 tháng', discountPercent: 0 },
  { months: 3, days: 90, label: '3 tháng', discountPercent: 10 },
  { months: 12, days: 365, label: '1 năm', discountPercent: 20 },
  { months: 24, days: 730, label: '2 năm', discountPercent: 30 },
] as const;

export type SubscriptionPurchaseDuration =
  (typeof SUBSCRIPTION_PURCHASE_DURATIONS)[number];

export function getSubscriptionPurchaseDuration(
  months: number,
): SubscriptionPurchaseDuration {
  return (
    SUBSCRIPTION_PURCHASE_DURATIONS.find((item) => item.months === months) ??
    SUBSCRIPTION_PURCHASE_DURATIONS[0]
  );
}

export function subscriptionCheckoutAmounts(monthlyPrice: number, months: number) {
  const duration = getSubscriptionPurchaseDuration(months);
  const baseVnd = monthlyPrice * duration.months;
  const discountVnd = Math.round(baseVnd * (duration.discountPercent / 100));
  return {
    baseVnd,
    discountPercent: duration.discountPercent,
    discountVnd,
    totalVnd: baseVnd - discountVnd,
  };
}

export const SUBSCRIPTION_PLAN_LABELS: Record<string, string> = {
  free: 'Miễn phí',
  basic: 'GÓI CƠ BẢN',
  standard: 'GÓI TRUNG',
  premium: 'GÓI LỚN',
  theo_yeu_cau: 'GÓI THEO YÊU CẦU',
  pro: 'GÓI CƠ BẢN',
  elite: 'GÓI TRUNG',
};

/** Chuẩn hóa subscription_level từ API (hỗ trợ legacy pro/elite). */
export function normalizeSubscriptionLevel(value: unknown): SubscriptionLevel {
  if (value == null || value === '') return 'free';
  const s = String(value).trim().toLowerCase().replace(/-/g, '_');
  if (s in LEGACY_SUBSCRIPTION_LEVEL_MAP) {
    return LEGACY_SUBSCRIPTION_LEVEL_MAP[s as keyof typeof LEGACY_SUBSCRIPTION_LEVEL_MAP];
  }
  if (s === 'basic' || s === 'standard' || s === 'premium' || s === 'theo_yeu_cau') {
    return s;
  }
  if (s === 'unlimited' || s === 'on_request') {
    return 'theo_yeu_cau';
  }
  if (s !== 'free' && s !== 'none') {
    return 'theo_yeu_cau';
  }
  return 'free';
}

export function parseSubscriptionEndDate(value?: string | null): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function isOwnerSubscriptionActive(owner?: OwnerSubscriptionInfo | null): boolean {
  if (!owner) return false;
  if (owner.is_subscription_active === true) {
    const end = parseSubscriptionEndDate(owner.subscription_end_date);
    return !end || end.getTime() > Date.now();
  }

  const level = normalizeSubscriptionLevel(owner.subscription_level);
  if (level === 'free') {
    return false;
  }

  const end = parseSubscriptionEndDate(owner.subscription_end_date);
  if (!end) {
    return true;
  }
  return end.getTime() > Date.now();
}

export function getEffectiveSubscriptionLevel(owner?: OwnerSubscriptionInfo | null): string {
  if (!isOwnerSubscriptionActive(owner)) return 'free';
  return normalizeSubscriptionLevel(owner?.subscription_level);
}

export function getPropertyLimitForOwner(owner?: OwnerSubscriptionInfo | null): number {
  const level = getEffectiveSubscriptionLevel(owner);
  return SUBSCRIPTION_PROPERTY_LIMITS[level] ?? 0;
}

export function canPlanCoverListingCount(planId: string | null | undefined, listingCount: number): boolean {
  const level = normalizeSubscriptionLevel(planId);
  if (level === 'free') return listingCount <= 0;
  const limit = SUBSCRIPTION_PROPERTY_LIMITS[level] ?? 0;
  if (!Number.isFinite(limit)) return true;
  return listingCount <= limit;
}

const PAID_PLAN_ORDER: SubscriptionLevel[] = ['basic', 'standard', 'premium', 'theo_yeu_cau'];

/** Gói thấp nhất đủ hạn mức cho số tin đang đăng (basic 15, standard 30, premium 50, theo yêu cầu). */
export function getMinimumPlanForListingCount(listingCount: number): SubscriptionLevel {
  const count = Math.max(0, Math.floor(Number(listingCount) || 0));
  for (const plan of PAID_PLAN_ORDER) {
    const limit = SUBSCRIPTION_PROPERTY_LIMITS[plan];
    if (!Number.isFinite(limit) || count <= limit) {
      return plan;
    }
  }
  return 'theo_yeu_cau';
}

export function getUpgradePlanForNextListing(currentCount: number, adding = 1): SubscriptionLevel {
  return getMinimumPlanForListingCount(currentCount + adding);
}

/** True nếu số căn hiện có + số căn sắp đăng vượt hạn mức gói. limit = null nghĩa là không giới hạn. */
export function wouldExceedPropertyLimit(
  listedCount: number,
  adding: number,
  limit: number | null | undefined,
): boolean {
  if (limit === null || limit === undefined) return false;
  if (!Number.isFinite(limit)) return false;
  if (limit <= 0) return adding > 0;
  return listedCount + adding > limit;
}

export function isUserPaidSubscriptionActive(user?: {
  subscriptionLevel?: string | null;
  subscriptionEndDate?: Date | string | null;
  isSubscriptionActive?: boolean;
  isPremium?: boolean;
} | null): boolean {
  if (!user) return false;
  const endIso =
    user.subscriptionEndDate instanceof Date
      ? (Number.isNaN(user.subscriptionEndDate.getTime()) ? null : user.subscriptionEndDate.toISOString())
      : user.subscriptionEndDate;
  if (user.isSubscriptionActive === true) {
    if (!endIso) return true;
    const end = parseSubscriptionEndDate(endIso);
    return !end || end.getTime() > Date.now();
  }
  return isOwnerSubscriptionActive({
    subscription_level: user.subscriptionLevel,
    subscription_end_date: endIso,
    is_subscription_active: user.isSubscriptionActive,
  });
}

export function canOwnerCreateProperty(
  owner: OwnerSubscriptionInfo | null | undefined,
  currentCount: number,
  adding = 1,
): boolean {
  const limit = getPropertyLimitForOwner(owner);
  if (!Number.isFinite(limit)) return true;
  return limit > 0 && currentCount + adding <= limit;
}

/** Không giới hạn hiển thị lưới công khai — giới hạn chỉ áp dụng khi đăng tin. */
export function shouldLimitOwnerListings(
  _owner: OwnerSubscriptionInfo | null | undefined,
  _sameOwnerCountBefore: number,
  _isProductOwner: boolean,
): boolean {
  return false;
}
